import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { enhancedApi } from '../api';
import './AddUserPage.css';

const AddUserPage = ({ onInviteSubmit, onClose, isDialog = false }) => {
  const [step, setStep] = useState(1);
  const [includeMessage, setIncludeMessage] = useState(false);
  const [email, setEmail] = useState('');
  const [license, setLicense] = useState('');
  const [permissionSet, setPermissionSet] = useState('staff_assigned_clients');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [companyName, setCompanyName] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.company_name || 'Your Firm';
    } catch { return 'Your Firm'; }
  });
  const [personalMessage, setPersonalMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [inviteeName, setInviteeName] = useState('');
  const [inviteeRole, setInviteeRole] = useState('');
  const [expiryDays, setExpiryDays] = useState(7);
  const navigate = useNavigate();

  // Email templates
  const emailTemplates = {
    default: {
      subject: `You're invited to join ${companyName}`,
      body: `Hi there,

You've been invited to join ${companyName} as a team member. This platform will help you collaborate on tax auditing projects and manage client files efficiently.

Your role: {role}
Access level: {permission_level}

Please click the link below to accept this invitation and set up your account:

{invitation_link}

This invitation will expire in {expiry_days} days.

Best regards,
The ${companyName} Team`
    },
    formal: {
      subject: `Professional Invitation to Join ${companyName}`,
      body: `Dear {invitee_name},

We are pleased to extend a professional invitation for you to join ${companyName} as a valued team member. Your expertise will contribute significantly to our collaborative tax auditing environment.

Role: {role}
Permission Level: {permission_level}

To proceed with your account setup, please access the following secure link:

{invitation_link}

This invitation is valid for {expiry_days} days from the date of issue.

We look forward to your participation in our team.

Sincerely,
${companyName} Management`
    },
    casual: {
      subject: `Join our team at ${companyName}!`,
      body: `Hey {invitee_name}! 👋

Great news! You've been invited to join our awesome team at ${companyName}. We think you'll love working with our tax auditing platform.

Your role: {role}
What you'll have access to: {permission_level}

Ready to get started? Click here:

{invitation_link}

This invite is good for {expiry_days} days.

Can't wait to have you on board!

Cheers,
The ${companyName} Crew`
    }
  };

  // Debug function to check authentication status
  const checkAuthStatus = () => {
    // Check for all possible token keys
    const accessToken = localStorage.getItem('access_token');
    const jwtToken = localStorage.getItem('access_token') || localStorage.getItem('jwtToken');
    const token = accessToken || jwtToken; // Use whichever is available
    
    const userId = localStorage.getItem('userId');
    const user = localStorage.getItem('user');
    
    // Try to get userId from user object if not directly available
    let finalUserId = userId;
    if (!finalUserId && user) {
      try {
        const userData = JSON.parse(user);
        finalUserId = userData.id || userData.user_id;
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    
    return { hasToken: !!token, userId: finalUserId };
  };

  // Check authentication on component mount and fetch company name
  useEffect(() => {
    const { hasToken, userId } = checkAuthStatus();
    if (!hasToken && !userId) {
      setError('Please log in to continue.');
      return;
    }
    // Fetch company name from API if not already set from localStorage
    if (!companyName || companyName === 'Your Firm') {
      enhancedApi.getCompanyInformation().then(res => {
        const name = res.data?.data?.name || res.data?.name;
        if (name) setCompanyName(name);
      }).catch(() => {});
    }
  }, []);

  const handleToggle = () => {
    setIncludeMessage(!includeMessage);
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (!email || !/\S+@\S+\.\S+/.test(email)) {
        setError('Please enter a valid email address');
        return;
      }
      if (!inviteeName.trim()) {
        setError('Please enter the invitee\'s name');
        return;
      }
      setError('');
      setStep(2);
    } else if (step === 2) {
      if (!license) {
        setError('Please select a license');
        return;
      }
      setError('');
      setStep(3);
    }
  };

  const handlePreviousStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setError('');
    }
  };

  const generateEmailPreview = () => {
    const template = emailTemplates[selectedTemplate];
    const preview = template.body
      .replace('{invitee_name}', inviteeName || '[Name]')
      .replace('{role}', inviteeRole || '[Role]')
      .replace('{permission_level}', getPermissionDisplayName(permissionSet))
      .replace('{invitation_link}', '[Invitation Link]')
      .replace('{expiry_days}', expiryDays);

    return {
      subject: template.subject,
      body: includeMessage && personalMessage ? 
        `${preview}\n\nPersonal Message:\n${personalMessage}` : 
        preview
    };
  };

  const getPermissionDisplayName = (permission) => {
    const permissionNames = {
      'admin': 'Full Administrator Access',
      'admin_no_billing': 'Administrator (No Billing Access)',
      'all_access': 'All Access',
      'custom_ai_settings': 'Custom AI Settings',
      'staff_all_clients': 'Staff (All Clients)',
      'staff_all_clients_no_billing': 'Staff (All Clients, No billing)',
      'staff_assigned_clients': 'Staff (Assigned Clients)',
      'staff_assigned_clients_no_billing': 'Staff (Assigned Clients, No billing)'
    };
    return permissionNames[permission] || permission;
  };

  const handleInvite = async () => {
    try {
      setIsLoading(true);
      const { hasToken, userId } = checkAuthStatus();
      
      if (!hasToken) {
        setError('You are not logged in. Please log in again.');
        navigate('/login');
        return;
      }

      if (!userId) {
        setError('User ID not found. Please log in again.');
        navigate('/login');
        return;
      }

      const invitationData = {
        email,
        role: permissionSet,
        inviteeName,
        inviteeRole,
        personalMessage: includeMessage ? personalMessage : '',
        template: selectedTemplate,
        expiryDays,
        companyName
      };

      const response = await enhancedApi.inviteUser(invitationData);

      setError('');
      setSuccessMessage('Invitation sent successfully!');
      if (onInviteSubmit) onInviteSubmit();
      if (onClose) onClose();
    } catch (err) {
      if (err.response?.status === 401) {
        setError('You are not authorized. Please log in again.');
        // Clear all possible token keys
        localStorage.removeItem('access_token');
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        navigate('/login');
      } else {
        setError(err.response?.data?.message || 'Failed to add user. Please try again.');
      }
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    // Don't navigate to home, just close the dialog
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      <div className={`step ${step >= 1 ? 'active' : ''}`}>
        <div className="step-number">1</div>
        <div className="step-label">Invite Details</div>
      </div>
      <div className={`step-connector ${step >= 2 ? 'active' : ''}`}></div>
      <div className={`step ${step >= 2 ? 'active' : ''}`}>
        <div className="step-number">2</div>
        <div className="step-label">Access & Permissions</div>
      </div>
      <div className={`step-connector ${step >= 3 ? 'active' : ''}`}></div>
      <div className={`step ${step >= 3 ? 'active' : ''}`}>
        <div className="step-number">3</div>
        <div className="step-label">Review & Send</div>
      </div>
    </div>
  );

  const content = (
    <div className={`modal ${isDialog ? 'dialog-content' : 'enhanced-modal'}`}>
      {/* Header */}
      <div className="modal-header">
        <div className="header-content">
          <div className="company-branding">
            <div className="company-logo">🏢</div>
            <div className="company-info">
              <h3>{companyName}</h3>
              <span className="invitation-type">Team Invitation</span>
            </div>
          </div>
          {renderStepIndicator()}
        </div>
        <button className="close-btn" onClick={handleClose}>✕</button>
      </div>

      {/* Body */}
      <div className="modal-body">
        {step === 1 && (
          <div className="step-content">
            <div className="step-header">
              <div className="step-icon">👤</div>
              <div>
                <h2>Invite Team Member</h2>
                <p>Add a new team member to collaborate on your projects</p>
              </div>
            </div>
            
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    placeholder="Enter full name"
                    value={inviteeName}
                    onChange={(e) => setInviteeName(e.target.value)}
                    className="form-input"
                    autoComplete="off"
                    spellCheck="false"
                  />
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    placeholder="colleague@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                    autoComplete="off"
                    spellCheck="false"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Job Title / Role</label>
                <input
                  type="text"
                  placeholder="e.g., Tax Associate, Senior Auditor"
                  value={inviteeRole}
                  onChange={(e) => setInviteeRole(e.target.value)}
                  className="form-input"
                  autoComplete="off"
                  spellCheck="false"
                />
              </div>

              <div className="form-group">
                <label>Invitation Expiry</label>
                <select
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(parseInt(e.target.value))}
                  className="form-input"
                >
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>

              {error && <p className="error-message">{error}</p>}
            </div>
            
            <div className="step-actions">
              <button className="btn-secondary" onClick={handleClose}>Cancel</button>
              <button className="btn-primary" onClick={handleNextStep}>Continue</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="step-content">
            <div className="step-header">
              <div className="step-icon">🔐</div>
              <div>
                <h2>Access & Permissions</h2>
                <p>Configure what this team member can access and do</p>
              </div>
            </div>
            
            <div className="form-section">
              <div className="form-group">
                <label>License Type *</label>
                <select
                  value={license}
                  onChange={(e) => setLicense(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select a license</option>
                  <option value="basic">Basic License - Essential features</option>
                  <option value="pro">Pro License - Advanced features</option>
                  <option value="enterprise">Enterprise License - Full access</option>
                </select>
              </div>

              <div className="form-group">
                <label>Permission Set *</label>
                <select
                  value={permissionSet}
                  onChange={(e) => setPermissionSet(e.target.value)}
                  className="form-input"
                >
                  <option value="admin">Administrator - Full system access</option>
                  <option value="admin_no_billing">Administrator (No billing) - Full access except billing</option>
                  <option value="all_access">All Access - Complete feature access</option>
                  <option value="custom_ai_settings">Custom AI Settings - AI features with customization</option>
                  <option value="staff_all_clients">Staff (All clients) - Access to all client data</option>
                  <option value="staff_all_clients_no_billing">Staff (All clients, No billing) - All clients without billing</option>
                  <option value="staff_assigned_clients">Staff (Assigned clients) - Only assigned client access</option>
                  <option value="staff_assigned_clients_no_billing">Staff (Assigned clients, No billing) - Assigned clients without billing</option>
                </select>
              </div>

              <div className="permission-preview">
                <h4>Access Summary</h4>
                <div className="permission-item">
                  <span className="permission-icon">📁</span>
                  <span>Client Files: {permissionSet.includes('all_clients') ? 'All Clients' : 'Assigned Clients Only'}</span>
                </div>
                <div className="permission-item">
                  <span className="permission-icon">💰</span>
                  <span>Billing Access: {permissionSet.includes('no_billing') ? 'No' : 'Yes'}</span>
                </div>
                <div className="permission-item">
                  <span className="permission-icon">🤖</span>
                  <span>AI Features: {permissionSet.includes('custom_ai') ? 'Custom Settings' : 'Standard'}</span>
                </div>
              </div>

              {error && <p className="error-message">{error}</p>}
            </div>
            
            <div className="step-actions">
              <button className="btn-secondary" onClick={handlePreviousStep}>Back</button>
              <button className="btn-primary" onClick={handleNextStep}>Continue</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="step-content">
            <div className="step-header">
              <div className="step-icon">✉️</div>
              <div>
                <h2>Review & Send Invitation</h2>
                <p>Customize the invitation message and send</p>
              </div>
            </div>
            
            <div className="form-section">
              <div className="form-group">
                <label>Email Template</label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="form-input"
                >
                  <option value="default">Professional (Default)</option>
                  <option value="formal">Formal</option>
                  <option value="casual">Casual</option>
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeMessage}
                    onChange={handleToggle}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  Include a personal message
                </label>
              </div>

              {includeMessage && (
                <div className="form-group">
                  <label>Personal Message</label>
                  <textarea
                    placeholder="Add a personal note to your invitation..."
                    value={personalMessage}
                    onChange={(e) => setPersonalMessage(e.target.value)}
                    className="form-input textarea"
                    rows={4}
                    autoComplete="off"
                    spellCheck="false"
                  />
                </div>
              )}

              <div className="email-preview-section">
                <div className="preview-header">
                  <h4>Email Preview</h4>
                  <button 
                    className="btn-preview"
                    onClick={() => setShowEmailPreview(!showEmailPreview)}
                  >
                    {showEmailPreview ? 'Hide Preview' : 'Show Preview'}
                  </button>
                </div>
                
                {showEmailPreview && (
                  <div className="email-preview">
                    <div className="email-header">
                      <strong>Subject:</strong> {generateEmailPreview().subject}
                    </div>
                    <div className="email-body">
                      <pre>{generateEmailPreview().body}</pre>
                    </div>
                  </div>
                )}
              </div>

              <div className="invitation-summary">
                <h4>Invitation Summary</h4>
                <div className="summary-item">
                  <span>Recipient:</span>
                  <span>{inviteeName} ({email})</span>
                </div>
                <div className="summary-item">
                  <span>Role:</span>
                  <span>{inviteeRole || 'Not specified'}</span>
                </div>
                <div className="summary-item">
                  <span>Access Level:</span>
                  <span>{getPermissionDisplayName(permissionSet)}</span>
                </div>
                <div className="summary-item">
                  <span>Expires:</span>
                  <span>In {expiryDays} day{expiryDays !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {error && <p className="error-message">{error}</p>}
            </div>
            
            <div className="step-actions">
              <button className="btn-secondary" onClick={handlePreviousStep}>Back</button>
              <button 
                className="btn-primary" 
                onClick={handleInvite}
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // If used inside a Dialog, return just the form content without any modal wrapper
  if (isDialog) {
    return (
      <div style={{ padding: '20px', maxHeight: '80vh', overflowY: 'auto' }}>
        {/* Simple header for Dialog mode */}
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '20px',
          borderRadius: '8px',
          color: 'white',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              fontSize: '24px',
              background: 'rgba(255, 255, 255, 0.2)',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>🏢</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>{companyName}</h3>
              <span style={{ fontSize: '12px', opacity: 0.9 }}>Team Invitation</span>
            </div>
          </div>
        </div>

        {/* Step indicator for Dialog mode */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          marginBottom: '20px',
          justifyContent: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderRadius: '16px',
            background: step >= 1 ? 'rgba(102, 126, 234, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            color: step >= 1 ? '#667eea' : '#666'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: step >= 1 ? '#667eea' : '#ccc',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '600'
            }}>1</div>
            <span style={{ fontSize: '12px', fontWeight: '500' }}>Details</span>
          </div>
          <div style={{
            width: '16px',
            height: '2px',
            background: step >= 2 ? '#667eea' : '#ccc'
          }}></div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderRadius: '16px',
            background: step >= 2 ? 'rgba(102, 126, 234, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            color: step >= 2 ? '#667eea' : '#666'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: step >= 2 ? '#667eea' : '#ccc',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '600'
            }}>2</div>
            <span style={{ fontSize: '12px', fontWeight: '500' }}>Permissions</span>
          </div>
          <div style={{
            width: '16px',
            height: '2px',
            background: step >= 3 ? '#667eea' : '#ccc'
          }}></div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            borderRadius: '16px',
            background: step >= 3 ? 'rgba(102, 126, 234, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            color: step >= 3 ? '#667eea' : '#666'
          }}>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: step >= 3 ? '#667eea' : '#ccc',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: '600'
            }}>3</div>
            <span style={{ fontSize: '12px', fontWeight: '500' }}>Send</span>
          </div>
        </div>

        {/* Form content */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 8px 0', color: '#2d3748' }}>
                Invite Team Member
              </h2>
              <p style={{ fontSize: '14px', color: '#718096', margin: 0 }}>
                Add a new team member to collaborate on your projects
              </p>
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4a5568', marginBottom: '8px' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter full name"
                    value={inviteeName}
                    onChange={(e) => setInviteeName(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4a5568', marginBottom: '8px' }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    placeholder="colleague@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4a5568', marginBottom: '8px' }}>
                  Job Title / Role
                </label>
                <input
                  type="text"
                  placeholder="e.g., Tax Associate, Senior Auditor"
                  value={inviteeRole}
                  onChange={(e) => setInviteeRole(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4a5568', marginBottom: '8px' }}>
                  Invitation Expiry
                </label>
                <select
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value={1}>1 day</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
              </div>

              {error && (
                <p style={{
                  color: '#e53e3e',
                  fontSize: '14px',
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: '#fed7d7',
                  borderRadius: '6px',
                  borderLeft: '4px solid #e53e3e'
                }}>{error}</p>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
              <button 
                onClick={handleClose}
                style={{
                  background: 'transparent',
                  color: '#4a5568',
                  border: '2px solid #e2e8f0',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleNextStep}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 8px 0', color: '#2d3748' }}>
                Access & Permissions
              </h2>
              <p style={{ fontSize: '14px', color: '#718096', margin: 0 }}>
                Configure what this team member can access and do
              </p>
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4a5568', marginBottom: '8px' }}>
                  License Type *
                </label>
                <select
                  value={license}
                  onChange={(e) => setLicense(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="">Select a license</option>
                  <option value="basic">Basic License - Essential features</option>
                  <option value="pro">Pro License - Advanced features</option>
                  <option value="enterprise">Enterprise License - Full access</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4a5568', marginBottom: '8px' }}>
                  Permission Set *
                </label>
                <select
                  value={permissionSet}
                  onChange={(e) => setPermissionSet(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="admin">Administrator - Full system access</option>
                  <option value="admin_no_billing">Administrator (No billing) - Full access except billing</option>
                  <option value="all_access">All Access - Complete feature access</option>
                  <option value="custom_ai_settings">Custom AI Settings - AI features with customization</option>
                  <option value="staff_all_clients">Staff (All clients) - Access to all client data</option>
                  <option value="staff_all_clients_no_billing">Staff (All clients, No billing) - All clients without billing</option>
                  <option value="staff_assigned_clients">Staff (Assigned clients) - Only assigned client access</option>
                  <option value="staff_assigned_clients_no_billing">Staff (Assigned clients, No billing) - Assigned clients without billing</option>
                </select>
              </div>

              <div style={{
                background: '#f7fafc',
                borderRadius: '12px',
                padding: '20px',
                marginTop: '20px'
              }}>
                <h4 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
                  Access Summary
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', fontSize: '14px', color: '#4a5568' }}>
                  <span style={{ fontSize: '16px' }}>📁</span>
                  <span>Client Files: {permissionSet.includes('all_clients') ? 'All Clients' : 'Assigned Clients Only'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', fontSize: '14px', color: '#4a5568' }}>
                  <span style={{ fontSize: '16px' }}>💰</span>
                  <span>Billing Access: {permissionSet.includes('no_billing') ? 'No' : 'Yes'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '14px', color: '#4a5568' }}>
                  <span style={{ fontSize: '16px' }}>🤖</span>
                  <span>AI Features: {permissionSet.includes('custom_ai') ? 'Custom Settings' : 'Standard'}</span>
                </div>
              </div>

              {error && (
                <p style={{
                  color: '#e53e3e',
                  fontSize: '14px',
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: '#fed7d7',
                  borderRadius: '6px',
                  borderLeft: '4px solid #e53e3e'
                }}>{error}</p>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
              <button 
                onClick={handlePreviousStep}
                style={{
                  background: 'transparent',
                  color: '#4a5568',
                  border: '2px solid #e2e8f0',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Back
              </button>
              <button 
                onClick={handleNextStep}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 8px 0', color: '#2d3748' }}>
                Review & Send Invitation
              </h2>
              <p style={{ fontSize: '14px', color: '#718096', margin: 0 }}>
                Customize the invitation message and send
              </p>
            </div>
            
            <div style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4a5568', marginBottom: '8px' }}>
                  Email Template
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="default">Professional (Default)</option>
                  <option value="formal">Formal</option>
                  <option value="casual">Casual</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '14px', color: '#4a5568' }}>
                  <input
                    type="checkbox"
                    checked={includeMessage}
                    onChange={handleToggle}
                    style={{ display: 'none' }}
                  />
                  <span style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '4px',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    background: includeMessage ? '#667eea' : 'transparent',
                    borderColor: includeMessage ? '#667eea' : 'divider'
                  }}>
                    {includeMessage && (
                      <span style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>✓</span>
                    )}
                  </span>
                  Include a personal message
                </label>
              </div>

              {includeMessage && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#4a5568', marginBottom: '8px' }}>
                    Personal Message
                  </label>
                  <textarea
                    placeholder="Add a personal note to your invitation..."
                    value={personalMessage}
                    onChange={(e) => setPersonalMessage(e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      resize: 'vertical',
                      minHeight: '100px'
                    }}
                  />
                </div>
              )}

              <div style={{ marginTop: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
                    Email Preview
                  </h4>
                  <button 
                    onClick={() => setShowEmailPreview(!showEmailPreview)}
                    style={{
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {showEmailPreview ? 'Hide Preview' : 'Show Preview'}
                  </button>
                </div>
                
                {showEmailPreview && (
                  <div style={{
                    background: '#f7fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginTop: '12px'
                  }}>
                    <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0', fontSize: '14px', color: '#4a5568' }}>
                      <strong>Subject:</strong> {generateEmailPreview().subject}
                    </div>
                    <div style={{ fontSize: '13px', color: '#2d3748', lineHeight: '1.5' }}>
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{generateEmailPreview().body}</pre>
                    </div>
                  </div>
                )}
              </div>

              <div style={{
                background: '#f0fff4',
                border: '1px solid #9ae6b4',
                borderRadius: '12px',
                padding: '20px',
                marginTop: '24px'
              }}>
                <h4 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#22543d' }}>
                  Invitation Summary
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', fontSize: '14px' }}>
                  <span style={{ color: '#4a5568', fontWeight: '500' }}>Recipient:</span>
                  <span style={{ color: '#2d3748', fontWeight: '600' }}>{inviteeName} ({email})</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', fontSize: '14px' }}>
                  <span style={{ color: '#4a5568', fontWeight: '500' }}>Role:</span>
                  <span style={{ color: '#2d3748', fontWeight: '600' }}>{inviteeRole || 'Not specified'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', fontSize: '14px' }}>
                  <span style={{ color: '#4a5568', fontWeight: '500' }}>Access Level:</span>
                  <span style={{ color: '#2d3748', fontWeight: '600' }}>{getPermissionDisplayName(permissionSet)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '14px' }}>
                  <span style={{ color: '#4a5568', fontWeight: '500' }}>Expires:</span>
                  <span style={{ color: '#2d3748', fontWeight: '600' }}>In {expiryDays} day{expiryDays !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {error && (
                <p style={{
                  color: '#e53e3e',
                  fontSize: '14px',
                  marginTop: '8px',
                  padding: '8px 12px',
                  background: '#fed7d7',
                  borderRadius: '6px',
                  borderLeft: '4px solid #e53e3e'
                }}>{error}</p>
              )}
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
              <button 
                onClick={handlePreviousStep}
                style={{
                  background: 'transparent',
                  color: '#4a5568',
                  border: '2px solid #e2e8f0',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                Back
              </button>
              <button 
                onClick={handleInvite}
                disabled={isLoading}
                style={{
                  background: isLoading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                {isLoading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Otherwise, return with modal overlay for standalone use
  return (
    <div className="modal-overlay">
      <div className="enhanced-modal">
        {/* Header */}
        <div className="modal-header">
          <div className="header-content">
            <div className="company-branding">
              <div className="company-logo">🏢</div>
              <div className="company-info">
                <h3>{companyName}</h3>
                <span className="invitation-type">Team Invitation</span>
              </div>
            </div>
            {renderStepIndicator()}
          </div>
          <button className="close-btn" onClick={handleClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {step === 1 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-icon">👤</div>
                <div>
                  <h2>Invite Team Member</h2>
                  <p>Add a new team member to collaborate on your projects</p>
                </div>
              </div>
              
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      placeholder="Enter full name"
                      value={inviteeName}
                      onChange={(e) => setInviteeName(e.target.value)}
                      className="form-input"
                      autoComplete="off"
                      spellCheck="false"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      placeholder="colleague@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="form-input"
                      autoComplete="off"
                      spellCheck="false"
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Job Title / Role</label>
                  <input
                    type="text"
                    placeholder="e.g., Tax Associate, Senior Auditor"
                    value={inviteeRole}
                    onChange={(e) => setInviteeRole(e.target.value)}
                    className="form-input"
                    autoComplete="off"
                    spellCheck="false"
                  />
                </div>

                <div className="form-group">
                  <label>Invitation Expiry</label>
                  <select
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(parseInt(e.target.value))}
                    className="form-input"
                  >
                    <option value={1}>1 day</option>
                    <option value={3}>3 days</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                  </select>
                </div>

                {error && <p className="error-message">{error}</p>}
              </div>
              
              <div className="step-actions">
                <button className="btn-secondary" onClick={handleClose}>Cancel</button>
                <button className="btn-primary" onClick={handleNextStep}>Continue</button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-icon">🔐</div>
                <div>
                  <h2>Access & Permissions</h2>
                  <p>Configure what this team member can access and do</p>
                </div>
              </div>
              
              <div className="form-section">
                <div className="form-group">
                  <label>License Type *</label>
                  <select
                    value={license}
                    onChange={(e) => setLicense(e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select a license</option>
                    <option value="basic">Basic License - Essential features</option>
                    <option value="pro">Pro License - Advanced features</option>
                    <option value="enterprise">Enterprise License - Full access</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Permission Set *</label>
                  <select
                    value={permissionSet}
                    onChange={(e) => setPermissionSet(e.target.value)}
                    className="form-input"
                  >
                    <option value="admin">Administrator - Full system access</option>
                    <option value="admin_no_billing">Administrator (No billing) - Full access except billing</option>
                    <option value="all_access">All Access - Complete feature access</option>
                    <option value="custom_ai_settings">Custom AI Settings - AI features with customization</option>
                    <option value="staff_all_clients">Staff (All clients) - Access to all client data</option>
                    <option value="staff_all_clients_no_billing">Staff (All clients, No billing) - All clients without billing</option>
                    <option value="staff_assigned_clients">Staff (Assigned clients) - Only assigned client access</option>
                    <option value="staff_assigned_clients_no_billing">Staff (Assigned clients, No billing) - Assigned clients without billing</option>
                  </select>
                </div>

                <div className="permission-preview">
                  <h4>Access Summary</h4>
                  <div className="permission-item">
                    <span className="permission-icon">📁</span>
                    <span>Client Files: {permissionSet.includes('all_clients') ? 'All Clients' : 'Assigned Clients Only'}</span>
                  </div>
                  <div className="permission-item">
                    <span className="permission-icon">💰</span>
                    <span>Billing Access: {permissionSet.includes('no_billing') ? 'No' : 'Yes'}</span>
                  </div>
                  <div className="permission-item">
                    <span className="permission-icon">🤖</span>
                    <span>AI Features: {permissionSet.includes('custom_ai') ? 'Custom Settings' : 'Standard'}</span>
                  </div>
                </div>

                {error && <p className="error-message">{error}</p>}
              </div>
              
              <div className="step-actions">
                <button className="btn-secondary" onClick={handlePreviousStep}>Back</button>
                <button className="btn-primary" onClick={handleNextStep}>Continue</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="step-content">
              <div className="step-header">
                <div className="step-icon">✉️</div>
                <div>
                  <h2>Review & Send Invitation</h2>
                  <p>Customize the invitation message and send</p>
                </div>
              </div>
              
              <div className="form-section">
                <div className="form-group">
                  <label>Email Template</label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="form-input"
                  >
                    <option value="default">Professional (Default)</option>
                    <option value="formal">Formal</option>
                    <option value="casual">Casual</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeMessage}
                      onChange={handleToggle}
                      className="checkbox-input"
                    />
                    <span className="checkbox-custom"></span>
                    Include a personal message
                  </label>
                </div>

                {includeMessage && (
                  <div className="form-group">
                    <label>Personal Message</label>
                    <textarea
                      placeholder="Add a personal note to your invitation..."
                      value={personalMessage}
                      onChange={(e) => setPersonalMessage(e.target.value)}
                      className="form-input textarea"
                      rows={4}
                      autoComplete="off"
                      spellCheck="false"
                    />
                  </div>
                )}

                <div className="email-preview-section">
                  <div className="preview-header">
                    <h4>Email Preview</h4>
                    <button 
                      className="btn-preview"
                      onClick={() => setShowEmailPreview(!showEmailPreview)}
                    >
                      {showEmailPreview ? 'Hide Preview' : 'Show Preview'}
                    </button>
                  </div>
                  
                  {showEmailPreview && (
                    <div className="email-preview">
                      <div className="email-header">
                        <strong>Subject:</strong> {generateEmailPreview().subject}
                      </div>
                      <div className="email-body">
                        <pre>{generateEmailPreview().body}</pre>
                      </div>
                    </div>
                  )}
                </div>

                <div className="invitation-summary">
                  <h4>Invitation Summary</h4>
                  <div className="summary-item">
                    <span>Recipient:</span>
                    <span>{inviteeName} ({email})</span>
                  </div>
                  <div className="summary-item">
                    <span>Role:</span>
                    <span>{inviteeRole || 'Not specified'}</span>
                  </div>
                  <div className="summary-item">
                    <span>Access Level:</span>
                    <span>{getPermissionDisplayName(permissionSet)}</span>
                  </div>
                  <div className="summary-item">
                    <span>Expires:</span>
                    <span>In {expiryDays} day{expiryDays !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {error && <p className="error-message">{error}</p>}
              </div>
              
              <div className="step-actions">
                <button className="btn-secondary" onClick={handlePreviousStep}>Back</button>
                <button 
                  className="btn-primary" 
                  onClick={handleInvite}
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddUserPage;